import { type ElectorRecord, type Voter, outcomes, parties, statuses } from "./domain";
import { complete, dirty, type Draft, type ProjectDrafts } from "./drafts";
import { actions, button, element, heading, message, notice } from "./ui";

export function answerBadges(
  value: Pick<
    ElectorRecord,
    "general_outcome" | "general_party" | "local_outcome" | "local_party" | "voter_status"
  > | null,
): HTMLElement {
  const result = element("div", "answer-badges");
  if (!value) return result;
  for (const [key, label] of [
    ["general", "General"],
    ["local", "Local"],
  ] as const) {
    const outcome = value[`${key}_outcome`];
    if (outcome) {
      const party = value[`${key}_party`];
      result.append(
        element(
          "span",
          `answer answer--${outcome}`,
          `${label}: ${outcomes.find(([id]) => id === outcome)![1]}${party ? ` · ${parties.find(([id]) => id === party)![1]}` : ""}`,
        ),
      );
    }
  }
  if (value.voter_status)
    result.append(
      element(
        "span",
        `answer answer--${value.voter_status}`,
        statuses.find(([id]) => id === value.voter_status)![1],
      ),
    );
  return result;
}

export class ElectorEditor {
  public readonly element = element("section", "elector-editor");
  private readonly unsubscribe: () => void;
  private readonly choices = element("div", "stack");
  private readonly status = notice();
  private readonly retry: HTMLButtonElement;
  public constructor(
    private readonly drafts: ProjectDrafts,
    private readonly voter: Voter,
    readonly: boolean,
  ) {
    const draft = drafts.get(voter.elector_id);
    if (!draft) throw new Error("The voter record has not loaded.");
    const notes = element("textarea");
    notes.rows = 4;
    notes.maxLength = 20_000;
    notes.value = draft.notes;
    notes.readOnly = readonly;
    notes.addEventListener("input", () => drafts.notes(voter.elector_id, notes.value));
    this.retry = button("Retry save", "button", () => drafts.save(voter.elector_id));
    this.element.append(
      heading(3, voter.name),
      this.choices,
      element("label", "field", "Note", notes),
      this.status,
      this.retry,
    );
    const render = () => {
      const value = drafts.get(voter.elector_id);
      if (!value) {
        this.element.replaceChildren();
        return;
      }
      this.renderChoices(value, readonly);
      this.status.className = value.error ? "error" : "muted";
      this.status.textContent = value.error
        ? `Couldn’t save: ${message(value.error)}. Your edits are retained in memory.`
        : !complete(value)
          ? "Choose a party for each Other Party answer before this result can be saved."
          : value.saving
            ? "Saving…"
            : dirty(value)
              ? "Unsaved edits"
              : "Saved";
      this.retry.hidden = readonly || !dirty(value) || !complete(value);
      this.retry.disabled = value.saving;
    };
    this.unsubscribe = drafts.subscribe(render);
    render();
  }
  private renderChoices(value: Draft, readonly: boolean): void {
    const children: HTMLElement[] = [];
    for (const [election, label] of [
      ["general", "General elections"],
      ["local", "Local elections"],
    ] as const) {
      const group = element("fieldset", "election-answers", element("legend", null, label));
      const row = actions(
        ...outcomes.map(([id, label]) => {
          const choice = button(
            label,
            `answer-choice answer-choice--${id}`,
            () => this.drafts.outcome(this.voter.elector_id, election, id),
            readonly,
          );
          choice.setAttribute("aria-pressed", String(value[`${election}_outcome`] === id));
          return choice;
        }),
      );
      group.append(row);
      if (value[`${election}_outcome`] === "other_party") {
        const partyGroup = element("div", "actions");
        partyGroup.setAttribute("aria-label", `${label} other party`);
        for (const [id, label] of parties) {
          const choice = button(
            label,
            "party-choice",
            () => this.drafts.party(this.voter.elector_id, election, id),
            readonly,
          );
          choice.setAttribute("aria-pressed", String(value[`${election}_party`] === id));
          partyGroup.append(choice);
        }
        group.append(element("p", "muted", "Other party (required)"), partyGroup);
      }
      children.push(group);
    }
    const shared = element(
      "fieldset",
      "election-answers",
      element("legend", null, "Voter status"),
      actions(
        ...statuses.map(([id, label]) => {
          const choice = button(
            label,
            `answer-choice answer-choice--${id}`,
            () => this.drafts.status(this.voter.elector_id, id),
            readonly,
          );
          choice.setAttribute("aria-pressed", String(value.voter_status === id));
          return choice;
        }),
      ),
    );
    children.push(shared);
    this.choices.replaceChildren(...children);
  }
  public dispose(): void {
    this.unsubscribe();
  }
}
